import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useAuthInfo } from '@sweetauth/core'

import { AUTH_COOKIE_HEADER_UPDATED_EVENT } from '@/lib/fetch/authHeader'

import { createPermissionLoader } from '@/services/permission-loader'
import { PermissionKey } from '@/services/permission-loader/types'
import DataLoader from '@/vendor/dataloader'

// When the provider is mounted but the loader isn't ready yet (e.g., no access token),
// we set context to null. If the provider is missing entirely, context will be undefined.
type LoaderContextType = DataLoader<PermissionKey, boolean> | null | undefined

const LoaderContext = createContext<LoaderContextType>(undefined)

type PermissionLoaderProviderProps = {
  children: React.ReactNode
}

export const PermissionLoaderProvider: React.FC<
  PermissionLoaderProviderProps
> = ({ children }) => {
  const {
    accessToken,
    user,
    tokens: { getAccessToken },
  } = useAuthInfo()
  const getAccessTokenRef = useRef(getAccessToken)
  const activeUserIdRef = useRef<string | null>(null)
  const loaderRef = useRef<DataLoader<PermissionKey, boolean> | null>(null)
  const [, setAuthCookieVersion] = useState(0)

  getAccessTokenRef.current = getAccessToken

  useEffect(() => {
    const handleAuthCookieUpdate = () => {
      loaderRef.current = null
      setAuthCookieVersion((version) => version + 1)
    }

    window.addEventListener(
      AUTH_COOKIE_HEADER_UPDATED_EVENT,
      handleAuthCookieUpdate,
    )
    return () => {
      window.removeEventListener(
        AUTH_COOKIE_HEADER_UPDATED_EVENT,
        handleAuthCookieUpdate,
      )
    }
  }, [])

  const activeUserId = user?.userId ?? null

  if (activeUserId !== activeUserIdRef.current) {
    activeUserIdRef.current = activeUserId
    loaderRef.current =
      activeUserId && accessToken
        ? createPermissionLoader(() => getAccessTokenRef.current())
        : null
  } else if (loaderRef.current === null && activeUserId && accessToken) {
    loaderRef.current = createPermissionLoader(() =>
      getAccessTokenRef.current(),
    )
  }

  const contextValue = activeUserId && accessToken ? loaderRef.current : null

  return (
    <LoaderContext.Provider value={contextValue}>
      {children}
    </LoaderContext.Provider>
  )
}

export const usePermissionLoader = () => {
  const context = useContext(LoaderContext)

  if (context === undefined) {
    throw new Error(
      'usePermissionLoader must be used within a PermissionLoaderProvider',
    )
  }

  return context
}
